import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';

/**
 * Auto-applies discount codes from copy-to-clipboard and Shopify `/discount/CODE` links.
 */

const DISCOUNT_COOKIE_NAME = 'discount_code';
const PENDING_DISCOUNT_KEY = 'pendingDiscountCode';
const POPUP_SHOWN_KEY = 'discountPopupShown';

/** @type {Promise<boolean> | null} */
let inflightSync = null;
/** True when any caller during the in-flight sync asked to show the popup. */
let popupRequested = false;
/** Skip re-applying the cookie code after the shopper removes it in the cart drawer. */
let discountCookieSyncOptOut = false;

/**
 * Stores a discount code for later application when cart becomes available
 * @param {string} discountCode - The discount code to store
 */
function storePendingDiscountCode(discountCode) {
  try {
    sessionStorage.setItem(PENDING_DISCOUNT_KEY, discountCode);
  } catch (error) {
  }
}

/**
 * Clears any pending discount code from session storage
 */
function clearPendingDiscountCode() {
  try {
    sessionStorage.removeItem(PENDING_DISCOUNT_KEY);
  } catch (error) {
  }
}

/**
 * Gets any pending discount code from session storage
 * @returns {string|null} - The pending discount code or null if none exists
 */
function getPendingDiscountCode() {
  try {
    const discountCode = sessionStorage.getItem(PENDING_DISCOUNT_KEY);
    return discountCode;
  } catch (error) {
    return null;
  }
}

/**
 * @param {string} name
 * @returns {string|null}
 */
function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;

  const cookies = document.cookie.split(';');
  for (const part of cookies) {
    const trimmed = part.trim();
    const separatorIndex = trimmed.indexOf('=');
    if (separatorIndex === -1) continue;
    if (trimmed.slice(0, separatorIndex) !== name) continue;
    try {
      return decodeURIComponent(trimmed.slice(separatorIndex + 1));
    } catch (error) {
      return trimmed.slice(separatorIndex + 1);
    }
  }

  return null;
}

/**
 * Discount code stored by Shopify after `/discount/CODE`.
 * @returns {string|null}
 */
function getShopifyDiscountCookie() {
  const value = getCookie(DISCOUNT_COOKIE_NAME);
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

/**
 * @returns {string|null}
 */
function getCodeToSync() {
  const cookieCode = getShopifyDiscountCookie();
  if (cookieCode) return cookieCode;
  return getPendingDiscountCode();
}

/**
 * @returns {string}
 */
function getCartJsonUrl() {
  if (typeof Theme !== 'undefined' && Theme.routes?.cart_url) {
    return `${String(Theme.routes.cart_url).replace(/\/?$/, '')}.js`;
  }
  return '/cart.js';
}

/**
 * @returns {string}
 */
function getCartUpdateUrl() {
  if (typeof Theme !== 'undefined' && Theme.routes?.cart_update_url) {
    return Theme.routes.cart_update_url;
  }
  return '/cart/update.js';
}

/**
 * Cart-drawer / cart-items section IDs only. Never the collection product grid.
 * @returns {string[]}
 */
function getCartDrawerSectionIds() {
  /** @type {string[]} */
  const sectionIds = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
    const sectionId = element.getAttribute('data-section-id');
    if (sectionId) sectionIds.push(sectionId);
  });
  return [...new Set(sectionIds)];
}

/**
 * @param {any} cart
 * @param {string} code
 * @returns {boolean}
 */
function cartHasDiscountCode(cart, code) {
  const codes = cart?.discount_codes;
  if (!Array.isArray(codes) || !code) return false;
  const needle = code.toLowerCase();
  return codes.some((/** @type {any} */ discount) => String(discount?.code || '').toLowerCase() === needle);
}

/**
 * @param {any} cart
 * @returns {string[]}
 */
function getExistingDiscountCodes(cart) {
  const codes = cart?.discount_codes;
  if (!Array.isArray(codes)) return [];
  return codes
    .map((/** @type {any} */ discount) => (typeof discount?.code === 'string' ? discount.code.trim() : ''))
    .filter(Boolean);
}

/**
 * @param {string} code
 */
function clearPendingIfMatches(code) {
  const pending = getPendingDiscountCode();
  if (!pending || !code) return;
  if (pending.trim().toLowerCase() === code.trim().toLowerCase()) {
    clearPendingDiscountCode();
  }
}

/**
 * Show the discount-applied popup once per tab session.
 * @param {string} discountCode
 */
function showDiscountCodePopupOnce(discountCode) {
  if (!discountCode) return;

  try {
    if (sessionStorage.getItem(POPUP_SHOWN_KEY)) return;
  } catch (error) {
  }

  const globalShow =
    typeof window !== 'undefined' ? /** @type {any} */ (window).showDiscountCodeNotification : null;

  if (typeof globalShow === 'function') {
    globalShow(discountCode);
  } else {
    const popup = document.getElementById('discount-code-popup');
    if (!popup) return;

    const closeButton = popup.querySelector('.popup-button--close');
    if (closeButton) {
      /** @type {any} */ (closeButton).onclick = () => {
        popup.classList.remove('show');
        setTimeout(() => {
          popup.classList.remove('popup-visible');
          popup.classList.add('popup-hidden');
        }, 300);
      };
    }

    popup.classList.remove('popup-hidden');
    popup.classList.add('popup-visible');
    setTimeout(() => {
      popup.classList.add('show');
    }, 10);

    setTimeout(() => {
      popup.classList.remove('show');
      setTimeout(() => {
        popup.classList.remove('popup-visible');
        popup.classList.add('popup-hidden');
      }, 300);
    }, 5000);
  }

  try {
    sessionStorage.setItem(POPUP_SHOWN_KEY, 'true');
  } catch (error) {
  }
}

/**
 * Submits a form and waits for the discount update event
 * @param {HTMLFormElement} form - The form to submit
 * @returns {Promise<void>} - Promise that resolves when discount update is complete
 */
function submitFormAsync(form) {
  return new Promise((resolve, reject) => {
    /** @type {(() => void) | undefined} */
    let handleDiscountUpdate;
    /** @type {number | undefined} */
    let timeoutId;

    try {
      handleDiscountUpdate = () => {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));
        clearTimeout(timeoutId);

        resolve();
      };

      timeoutId = setTimeout(() => {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));

        resolve();
      }, 3000); // Fallback timeout

      document.addEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));

      form.requestSubmit();
    } catch (error) {
      if (handleDiscountUpdate) {
        document.removeEventListener(ThemeEvents.discountUpdate, /** @type {EventListener} */ (handleDiscountUpdate));
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      reject(error);
    }
  });
}

/**
 * @param {string} discountCode
 * @returns {Promise<any | null>}
 */
async function postDiscountCode(discountCode) {
  const cartResponse = await fetch(getCartJsonUrl());
  if (!cartResponse.ok) return null;
  const cart = await cartResponse.json();

  if (cartHasDiscountCode(cart, discountCode)) {
    return cart;
  }

  const existingCodes = getExistingDiscountCodes(cart);
  const discount = [...new Set([...existingCodes, discountCode])].join(',');
  const sections = getCartDrawerSectionIds();

  /** @type {Record<string, unknown>} */
  const payload = { discount };
  if (sections.length > 0) {
    payload.sections = sections;
  }

  const response = await fetch(getCartUpdateUrl(), fetchConfig('json', { body: JSON.stringify(payload) }));
  if (!response.ok) return null;
  const data = await response.json();

  if (data.sections) {
    for (const sectionId of sections) {
      if (data.sections[sectionId]) {
        try {
          await morphSection(sectionId, data.sections[sectionId]);
        } catch (error) {
        }
      }
    }
  }

  if (cartHasDiscountCode(data, discountCode)) return data;

  const confirmResponse = await fetch(getCartJsonUrl());
  if (!confirmResponse.ok) return data;
  const confirmed = await confirmResponse.json();
  return cartHasDiscountCode(confirmed, discountCode) ? confirmed : data;
}

/**
 * Attach a discount code to the Ajax cart via `/cart/update.js`.
 * Overlapping calls share one request so a cart:update cannot drop the popup.
 * Morphs cart-drawer sections only.
 * @param {string} discountCode
 * @param {{ showPopup?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
async function applyDiscountViaCartApi(discountCode, options = {}) {
  const trimmedCode = discountCode.trim();
  if (!trimmedCode) return false;
  if (options.showPopup !== false) popupRequested = true;

  if (inflightSync) return inflightSync;

  inflightSync = (async () => {
    try {
      let data = await postDiscountCode(trimmedCode);
      if (!cartHasDiscountCode(data, trimmedCode)) {
        data = await postDiscountCode(trimmedCode);
      }

      const attached = cartHasDiscountCode(data, trimmedCode);
      if (!attached) return false;

      if (popupRequested) showDiscountCodePopupOnce(trimmedCode);
      popupRequested = false;
      clearPendingIfMatches(trimmedCode);
      return true;
    } catch (error) {
      return false;
    } finally {
      inflightSync = null;
    }
  })();

  return inflightSync;
}

/**
 * Apply the Shopify discount cookie (or a pending clipboard code) to the Ajax cart.
 * @param {{ showPopup?: boolean, ignoreOptOut?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
async function resyncStoredDiscountCode(options = {}) {
  try {
    if (!options.ignoreOptOut && discountCookieSyncOptOut) {
      const pendingCode = getPendingDiscountCode();
      if (!pendingCode) return false;
      return applyDiscountViaCartApi(pendingCode, { showPopup: options.showPopup !== false });
    }

    const code = getCodeToSync();
    if (!code) return false;
    return applyDiscountViaCartApi(code, { showPopup: options.showPopup !== false });
  } catch (error) {
    return false;
  }
}

/**
 * Shared function to automatically apply a discount code to the cart
 * @param {string} discountCode - The discount code to apply
 * @returns {Promise<boolean>} - Returns true if discount was applied successfully
 */
export async function autoApplyDiscountCode(discountCode) {
  try {
    if (!discountCode || typeof discountCode !== 'string') {
      return false;
    }

    const trimmedCode = discountCode.trim();
    if (!trimmedCode) {
      return false;
    }

    const appliedViaApi = await applyDiscountViaCartApi(trimmedCode, { showPopup: true });
    if (appliedViaApi) {
      return true;
    }

    const cartDrawer = document.querySelector('cart-drawer-component');
    const cartDiscountInput = cartDrawer ? cartDrawer.querySelector('#cart-discount') : document.querySelector('#cart-discount');

    if (!cartDiscountInput || !(cartDiscountInput instanceof HTMLInputElement)) {
      storePendingDiscountCode(trimmedCode);
      return false;
    }

    const cartDiscountForm = cartDiscountInput.closest('form');
    if (!cartDiscountForm) {
      storePendingDiscountCode(trimmedCode);
      return false;
    }

    cartDiscountInput.value = trimmedCode;

    await submitFormAsync(cartDiscountForm);

    showDiscountCodePopupOnce(trimmedCode);

    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Checks for and applies any pending discount codes when cart becomes available
 */
export async function checkAndApplyPendingDiscountCode() {
  try {
    await resyncStoredDiscountCode({ showPopup: false });
  } catch (error) {
  }
}

async function initDiscountCookieSync() {
  const synced = await resyncStoredDiscountCode({ showPopup: true, ignoreOptOut: true });
  if (synced) return;

  try {
    const cartResponse = await fetch(getCartJsonUrl());
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();
    const existingCode = getExistingDiscountCodes(cart)[0];
    if (existingCode) showDiscountCodePopupOnce(existingCode);
  } catch (error) {
  }
}

document.addEventListener(ThemeEvents.cartUpdate, async () => {
  try {
    await resyncStoredDiscountCode({ showPopup: false });
  } catch (error) {
  }
});

document.addEventListener(ThemeEvents.discountUpdate, (event) => {
  try {
    if (!(event instanceof DiscountUpdateEvent)) return;
    const resource = event.detail?.resource;
    const cookieCode = getShopifyDiscountCookie();
    if (!cookieCode || !resource) return;
    discountCookieSyncOptOut = !cartHasDiscountCode(resource, cookieCode);
  } catch (error) {
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiscountCookieSync);
} else {
  initDiscountCookieSync();
}